import {
  Router,
  Route,
  RootRoute,
  Outlet,
  redirect,
  createMemoryHistory,
  createBrowserHistory
} from '@tanstack/react-router';
import { useAuthStore } from '@/lib/auth';
import { get } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/routes/login';
import { DashboardPage } from '@/routes/dashboard';
import { ResidentsPage } from '@/routes/residents';
import { ResidentDetailPage } from '@/routes/resident-detail';
import { ResidentLedgerPage } from '@/routes/resident-ledger';
import { TowersPage } from '@/routes/towers';
import { GatesPage } from '@/routes/gates';
import { GuardsPage } from '@/routes/guards';
import { GuardDetailPage } from '@/routes/guard-detail';
import { VisitorLogsPage } from '@/routes/visitor-logs';
import { FrequentVisitorsPage } from '@/routes/frequent-visitors';
import { FrequentVisitorDetailPage } from '@/routes/frequent-visitor-detail';
import { HelperCategoriesPage } from '@/routes/helper-categories';
import { FamilyPage } from '@/routes/family';
import { NoticesPage } from '@/routes/notices';
import { SocietyEventsPage } from '@/routes/society-events';
import { SocietyEventDetailPage } from '@/routes/society-event-detail';
import { CommunityPage } from '@/routes/community';
import { HelpdeskPage } from '@/routes/helpdesk';
import { BillsPage } from '@/routes/bills';
import { ChargeTypesPage } from '@/routes/charge-types';
import { OneTimeChargesPage } from '@/routes/one-time-charges';
import { ReceiptsPage } from '@/routes/receipts';
import { ElectionsPage } from '@/routes/elections';
import { AmenitiesPage } from '@/routes/amenities';
import { VehiclesPage } from '@/routes/vehicles';
import { VendorsPage } from '@/routes/vendors';
import { VendorPortalPage } from '@/routes/vendor-portal';
import { AgenciesPage } from '@/routes/agencies';
import { AgencyDetailPage } from '@/routes/agency-detail';
import { StaffPage } from '@/routes/staff';
import { MoveInOutPage } from '@/routes/move-in-out';
import { ServiceProvidersPage } from '@/routes/service-providers';
import { ServiceProviderCategoriesPage } from '@/routes/service-provider-categories';
import { NocsPage } from '@/routes/nocs';
import { AuditLogPage } from '@/routes/audit-log';
import { ReportsPage } from '@/routes/reports';
import { CompliancePage } from '@/routes/compliance';
import { GovernancePage } from '@/routes/governance';
import { DocumentsPage } from '@/routes/documents';
import { AttendancePage } from '@/routes/attendance';
import { SettingsPage } from '@/routes/settings';
import { SuperAdminLoginPage } from '@/routes/superadmin-login';
import { SuperAdminDashboardPage } from '@/routes/superadmin-dashboard';
import { SuperAdminUsersPage } from '@/routes/superadmin-users';
import { SuperAdminAuditPage } from '@/routes/superadmin-audit';
import { SuperAdminTenantDetailPage } from '@/routes/superadmin-tenant-detail';
import { SuperAdminBillingPage } from '@/routes/superadmin-billing';
import { SuperAdminPlansPage } from '@/routes/superadmin-plans';
import { SuperAdminPermissionsPage } from '@/routes/superadmin-permissions';
import { SuperAdminMarketingLeadsPage } from '@/routes/superadmin-marketing-leads';
import { SuperAdminHrPage } from '@/routes/superadmin-hr';
import { OnboardingPage } from '@/routes/onboarding';
import { NotFoundPage } from '@/routes/not-found';
// Phase 3 parking — visual map editor, wrong-parker queue, EV strikes, analytics.
// Sibling agent owns the rest of the /parking surface (index, zone detail,
// disputes, marketplace, chargers, visitor parking). The four routes below
// are the additive Phase-3 surfaces.
import { ParkingMapPage } from '@/routes/parking-map';
import { ParkingWrongParkerPage } from '@/routes/parking-wrong-parker';
import { ParkingEvStrikesPage } from '@/routes/parking-ev-strikes';
import { ParkingAnalyticsPage } from '@/routes/parking-analytics';
// V0.3 parking module (this agent's lane):
//   /parking                        ParkingPage (zones / slots / allocations)
//   /parking/zones/$id              ParkingZoneDetailPage
//   /parking/disputes               ParkingDisputesPage
//   /parking/marketplace            ParkingMarketplacePage
//   /parking/chargers               ParkingChargersPage
//   /parking/chargers/$id           ParkingChargerDetailPage
//   /parking/charger-requests       ParkingChargerRequestsPage
//   /visitor-parking                VisitorParkingPage
import { ParkingPage } from '@/routes/parking';
import { ParkingZoneDetailPage } from '@/routes/parking-zone-detail';
import { PanicIncidentsPage } from '@/routes/panic-incidents';
import { SecurityIncidentsPage } from '@/routes/security-incidents';
import { PatrolPage } from '@/routes/patrol';
import { ParkingDisputesPage } from '@/routes/parking-disputes';
import { ParkingMarketplacePage } from '@/routes/parking-marketplace';
import { ParkingChargersPage } from '@/routes/parking-chargers';
import { ParkingChargerDetailPage } from '@/routes/parking-charger-detail';
import { ParkingChargerRequestsPage } from '@/routes/parking-charger-requests';
import { VisitorParkingPage } from '@/routes/visitor-parking';
import { CollectionsPage } from '@/routes/collections';
import { CollectionDetailPage } from '@/routes/collection-detail';
import { CamerasPage } from '@/routes/cameras';
import { CameraDetailPage } from '@/routes/camera-detail';
import { NotificationPreferencesPage } from '@/routes/notification-preferences';
import { SessionsPage } from '@/routes/sessions';
import { CommunityPollsPage } from '@/routes/community-polls';
import { LostFoundPage } from '@/routes/lost-found';
import { ChatPage } from '@/routes/chat';

const rootRoute = new RootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage
});

/**
 * Index route — routes a freshly-authenticated session to the right landing
 * page based on role. For tenant admins we also peek at the society's
 * `onboarding_completed_at` flag and bounce first-time admins into the
 * `/onboarding` wizard instead of `/dashboard`. The fetch is cached at the
 * server (it's the same /society/me query the dashboard issues immediately
 * after), and we swallow errors so a transient API hiccup never blocks
 * login.
 */
const indexRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    const auth = useAuthStore.getState();
    const isAuthed = Boolean(auth.accessToken);
    if (!isAuthed) throw redirect({ to: '/login' });
    if (auth.user?.role === 'super_admin') {
      throw redirect({ to: '/superadmin/dashboard' });
    }
    // Vendors land on their self-service portal — they have no admin chrome.
    if (auth.user?.role === 'vendor') {
      throw redirect({ to: '/vendor-portal' });
    }
    if (auth.user?.role === 'admin') {
      try {
        const society = await get<{ onboarding_completed_at: string | null }>('/society/me');
        if (!society?.onboarding_completed_at) {
          throw redirect({ to: '/onboarding' });
        }
      } catch (e) {
        // A redirect throw is the happy path — re-raise it; everything
        // else (network blip, 5xx) falls through to /dashboard so the user
        // is never trapped on a blank index.
        if (e instanceof Error && 'to' in (e as unknown as Record<string, unknown>)) throw e;
        // TanStack router uses a non-Error-instance signal for redirects in
        // some builds — surface anything with a `to` field too.
        if (e && typeof e === 'object' && 'to' in (e as Record<string, unknown>)) throw e;
      }
    }
    throw redirect({ to: '/dashboard' });
  }
});

const superAdminLoginRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/superadmin/login',
  component: SuperAdminLoginPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (auth.accessToken && auth.user?.role === 'super_admin') {
      throw redirect({ to: '/superadmin/dashboard' });
    }
  }
});

const superAdminDashboardRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/superadmin/dashboard',
  component: SuperAdminDashboardPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) throw redirect({ to: '/superadmin/login' });
    if (auth.user?.role !== 'super_admin') {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const superAdminUsersRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/superadmin/users',
  component: SuperAdminUsersPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) throw redirect({ to: '/superadmin/login' });
    if (auth.user?.role !== 'super_admin') {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const superAdminBillingRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/superadmin/billing',
  component: SuperAdminBillingPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) throw redirect({ to: '/superadmin/login' });
    if (auth.user?.role !== 'super_admin') {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const superAdminPlansRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/superadmin/plans',
  component: SuperAdminPlansPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) throw redirect({ to: '/superadmin/login' });
    if (auth.user?.role !== 'super_admin') {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const superAdminPermissionsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/superadmin/permissions',
  component: SuperAdminPermissionsPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) throw redirect({ to: '/superadmin/login' });
    if (auth.user?.role !== 'super_admin') {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const superAdminAuditRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/superadmin/audit',
  component: SuperAdminAuditPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) throw redirect({ to: '/superadmin/login' });
    if (auth.user?.role !== 'super_admin') {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const superAdminTenantDetailRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/superadmin/tenants/$id',
  component: SuperAdminTenantDetailPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) throw redirect({ to: '/superadmin/login' });
    if (auth.user?.role !== 'super_admin') {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const superAdminMarketingLeadsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/superadmin/marketing-leads',
  component: SuperAdminMarketingLeadsPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) throw redirect({ to: '/superadmin/login' });
    if (auth.user?.role !== 'super_admin') {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const superAdminHrRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/superadmin/hr',
  component: SuperAdminHrPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) throw redirect({ to: '/superadmin/login' });
    if (auth.user?.role !== 'super_admin') {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const loginRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (auth.accessToken) {
      // Bounce already-authed sessions to the right surface per role.
      if (auth.user?.role === 'vendor') throw redirect({ to: '/vendor-portal' });
      throw redirect({ to: '/dashboard' });
    }
  }
});

/**
 * Onboarding wizard — top-level route (no AppShell chrome). Sits outside
 * the auth layout because the wizard has its own header + progress bar and
 * the Sidebar would clutter a first-run experience. We still gate it on a
 * valid session: super-admins / vendors get bounced to their proper home,
 * and unauthenticated callers go to /login.
 */
const onboardingRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: OnboardingPage,
  beforeLoad: ({ location }) => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) {
      throw redirect({ to: '/login', search: { redirect: location.pathname } });
    }
    if (auth.user?.role === 'super_admin') {
      throw redirect({ to: '/superadmin/dashboard' });
    }
    if (auth.user?.role === 'vendor') {
      throw redirect({ to: '/vendor-portal' });
    }
  }
});

// Vendor portal — top-level (no admin Sidebar/AppShell). Vendors are an
// external contractor identity, so the admin chrome would be confusing and
// would leak society-wide nav surfaces they have no access to.
const vendorPortalRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/vendor-portal',
  component: VendorPortalPage,
  beforeLoad: () => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) throw redirect({ to: '/login' });
    if (auth.user?.role !== 'vendor') {
      // Role mismatch — most likely an admin hit this URL by mistake. Send
      // them to their own dashboard (super_admin handled by indexRoute).
      throw redirect({ to: '/' });
    }
  }
});

// ─── authenticated layout ──────────────────────────────────
const authLayoutRoute = new Route({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: AppShell,
  beforeLoad: ({ location }) => {
    const auth = useAuthStore.getState();
    if (!auth.accessToken) {
      throw redirect({
        to: '/login',
        search: { redirect: location.pathname }
      });
    }
    // Vendors should never land inside the admin shell — bounce them to
    // their portal so a stale URL or shared link can't expose admin nav.
    if (auth.user?.role === 'vendor') {
      throw redirect({ to: '/vendor-portal' });
    }
  }
});

/**
 * Reusable role-gate for routes that should NOT be reachable by accountant.
 * Accountant scope per V0.2 spec: Dashboard / Residents / Towers / Bills /
 * Documents only. Everything else bounces back to /dashboard so the URL bar
 * + bookmark are corrected and we never render an unauthorised page.
 */
const blockAccountant = () => {
  const role = useAuthStore.getState().user?.role;
  if (role === 'accountant') {
    throw redirect({ to: '/dashboard' });
  }
};

const dashboardRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/dashboard',
  component: DashboardPage
});

const residentsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/residents',
  component: ResidentsPage
});

const residentDetailRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/residents/$id',
  component: ResidentDetailPage,
  beforeLoad: blockAccountant
});

const residentLedgerRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/residents/$id/ledger',
  component: ResidentLedgerPage
});

const towersRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/towers',
  component: TowersPage
});

const gatesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/gates',
  component: GatesPage,
  beforeLoad: blockAccountant
});

const guardsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/guards',
  component: GuardsPage,
  beforeLoad: blockAccountant
});

const guardDetailRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/guards/$id',
  component: GuardDetailPage,
  beforeLoad: blockAccountant
});

const visitorLogsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/visitor-logs',
  component: VisitorLogsPage,
  beforeLoad: blockAccountant
});

const frequentVisitorsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/frequent-visitors',
  component: FrequentVisitorsPage,
  beforeLoad: blockAccountant
});

const frequentVisitorDetailRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/frequent-visitors/$id',
  component: FrequentVisitorDetailPage,
  beforeLoad: blockAccountant
});

// Helper categories — per-society FV kinds catalog CRUD. Any admin-rooted role
// can manage (server further restricts mutating ops to admin/building_manager/
// super_admin); accountant is blocked since the catalog is operational, not
// financial.
const helperCategoriesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/helper-categories',
  component: HelperCategoriesPage,
  beforeLoad: blockAccountant
});

const familyRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/family',
  component: FamilyPage,
  beforeLoad: blockAccountant
});

const noticesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/notices',
  component: NoticesPage,
  beforeLoad: blockAccountant
});

// Society events (community gatherings). Distinct from /governance which
// owns AGM/EGM meetings. Same role gate as Notices.
const societyEventsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/society-events',
  component: SocietyEventsPage,
  beforeLoad: blockAccountant
});

const societyEventDetailRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/society-events/$id',
  component: SocietyEventDetailPage,
  beforeLoad: blockAccountant
});

const helpdeskRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/helpdesk',
  component: HelpdeskPage,
  beforeLoad: blockAccountant
});

const communityRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/community',
  component: CommunityPage,
  beforeLoad: blockAccountant
});

const billsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/bills',
  component: BillsPage
});

// Charge types — per-society bill charge catalog (Phase 1 of the Charges
// module). Allowed for all admin-rooted finance roles (admin/super_admin/
// accountant/treasurer/president). The server further restricts mutating
// ops to the same set.
const chargeTypesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/charge-types',
  component: ChargeTypesPage,
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    // Allow accountant + finance committee. Block secretary / building_manager
    // since they don't own the financial catalog.
    const allowed = ['admin', 'super_admin', 'accountant', 'treasurer', 'president'];
    if (!role || !allowed.includes(role)) {
      throw redirect({ to: '/dashboard' });
    }
  }
});

// One-time charges + Receipts — Phase 3 of the Charges module. Same role
// gate as Charge types (finance committee only). Accountant + treasurer +
// president get access alongside admin.
const oneTimeChargesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/one-time-charges',
  component: OneTimeChargesPage,
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    const allowed = ['admin', 'super_admin', 'accountant', 'treasurer', 'president'];
    if (!role || !allowed.includes(role)) {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const receiptsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/receipts',
  component: ReceiptsPage,
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    const allowed = ['admin', 'super_admin', 'accountant', 'treasurer', 'president'];
    if (!role || !allowed.includes(role)) {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const amenitiesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/amenities',
  component: AmenitiesPage,
  beforeLoad: blockAccountant
});

const documentsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/documents',
  component: DocumentsPage
});

const vehiclesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/vehicles',
  component: VehiclesPage,
  beforeLoad: blockAccountant
});

const vendorsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/vendors',
  component: VendorsPage,
  beforeLoad: blockAccountant
});

const agenciesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/agencies',
  component: AgenciesPage,
  beforeLoad: blockAccountant
});

const agencyDetailRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/agencies/$id',
  component: AgencyDetailPage,
  beforeLoad: blockAccountant
});

const staffRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/staff',
  component: StaffPage
});

// Service Providers (community-curated directory). Visible to admin-rooted
// roles + secretary/president/building_manager (server enforces same).
// Accountant is blocked since this is an operational directory, not financial.
const serviceProvidersRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/service-providers',
  component: ServiceProvidersPage,
  beforeLoad: blockAccountant
});
const serviceProviderCategoriesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/service-provider-categories',
  component: ServiceProviderCategoriesPage,
  beforeLoad: blockAccountant
});

const moveInOutRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/move-in-out',
  component: MoveInOutPage,
  beforeLoad: blockAccountant
});

const nocsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/nocs',
  component: NocsPage,
  beforeLoad: blockAccountant
});

const auditLogRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/audit-log',
  component: AuditLogPage,
  beforeLoad: blockAccountant
});

// Reports — admin/super_admin/accountant/treasurer/president. Same role
// allow-list the server enforces; accountant included since the financial
// + defaulter reports are core to their day-to-day.
const reportsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/reports',
  component: ReportsPage,
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    const allowed = ['admin', 'super_admin', 'accountant', 'treasurer', 'president'];
    if (!role || !allowed.includes(role)) {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const complianceRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/compliance',
  component: CompliancePage,
  beforeLoad: blockAccountant
});

const governanceRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/governance',
  component: GovernancePage,
  beforeLoad: blockAccountant
});

const electionsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/elections',
  component: ElectionsPage,
  beforeLoad: blockAccountant
});

const attendanceRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/attendance',
  component: AttendancePage,
  beforeLoad: blockAccountant
});

const settingsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/settings',
  component: SettingsPage,
  beforeLoad: blockAccountant
});

// ─── V0.3 parking routes (this agent) ──────────────────────
const parkingRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking',
  component: ParkingPage,
  beforeLoad: blockAccountant
});

const parkingZoneDetailRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking/zones/$id',
  component: ParkingZoneDetailPage,
  beforeLoad: blockAccountant
});

const parkingDisputesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking/disputes',
  component: ParkingDisputesPage,
  beforeLoad: blockAccountant
});

const parkingMarketplaceRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking/marketplace',
  component: ParkingMarketplacePage,
  beforeLoad: blockAccountant
});

const parkingChargersRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking/chargers',
  component: ParkingChargersPage,
  beforeLoad: blockAccountant
});

const parkingChargerDetailRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking/chargers/$id',
  component: ParkingChargerDetailPage,
  beforeLoad: blockAccountant
});

const parkingChargerRequestsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking/charger-requests',
  component: ParkingChargerRequestsPage,
  beforeLoad: blockAccountant
});

const visitorParkingRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/visitor-parking',
  component: VisitorParkingPage,
  beforeLoad: blockAccountant
});

// ─── Phase 3 parking routes (sibling agent) ────────────────
// These are additive surfaces (visual map, wrong-parker queue, EV strikes,
// analytics). Path IDs deliberately distinct to avoid TanStack collision.
const parkingMapRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking/zones/$zoneId/map',
  component: ParkingMapPage,
  beforeLoad: blockAccountant
});

const parkingWrongParkerRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking/wrong-parker',
  component: ParkingWrongParkerPage,
  beforeLoad: blockAccountant
});

const parkingEvStrikesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking/ev-strikes',
  component: ParkingEvStrikesPage,
  beforeLoad: blockAccountant
});

const parkingAnalyticsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/parking/analytics',
  component: ParkingAnalyticsPage,
  beforeLoad: blockAccountant
});

// SOS / Panic incidents dashboard. Visible to admin/super_admin/president/
// secretary/building_manager — accountant + treasurer are finance-only and
// don't own incident response.
const panicIncidentsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/panic-incidents',
  component: PanicIncidentsPage,
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    const allowed = ['admin', 'super_admin', 'president', 'secretary', 'building_manager'];
    if (!role || !allowed.includes(role)) {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const securityIncidentsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/security-incidents',
  component: SecurityIncidentsPage,
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    const allowed = ['admin', 'super_admin', 'president', 'secretary', 'building_manager'];
    if (!role || !allowed.includes(role)) {
      throw redirect({ to: '/dashboard' });
    }
  }
});

const patrolRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/patrol',
  component: PatrolPage,
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    const allowed = ['admin', 'super_admin', 'building_manager', 'secretary'];
    if (!role || !allowed.includes(role)) {
      throw redirect({ to: '/dashboard' });
    }
  }
});

// Admin ↔ Guard chat. Operational module — same allow-list as security-incidents
// so the people triaging incidents can reply to the guards who raised them.
const chatRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/chat',
  component: ChatPage,
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    const allowed = ['admin', 'super_admin', 'president', 'secretary', 'building_manager'];
    if (!role || !allowed.includes(role)) {
      throw redirect({ to: '/dashboard' });
    }
  }
});

// Collections / Defaulters dashboard. Visible to admin/super_admin/accountant/
// treasurer/president — same finance roles that own bills + receipts.
const COLLECTIONS_ROLES = ['admin', 'super_admin', 'accountant', 'treasurer', 'president'] as const;
const guardCollections = (): void => {
  const role = useAuthStore.getState().user?.role;
  if (!role || !(COLLECTIONS_ROLES as ReadonlyArray<string>).includes(role)) {
    throw redirect({ to: '/dashboard' });
  }
};

const collectionsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/collections',
  component: CollectionsPage,
  beforeLoad: guardCollections
});

const collectionDetailRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/collections/$unitId',
  component: CollectionDetailPage,
  beforeLoad: guardCollections
});

// Community polls — admin/secretary/president/building_manager curate; all
// admin-rooted roles can read. Accountant + treasurer included read-only so
// they see resident sentiment that may relate to finance proposals.
const COMMUNITY_POLLS_ALLOWED = ['admin', 'super_admin', 'accountant', 'treasurer', 'secretary', 'president', 'building_manager'];
const communityPollsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/community-polls',
  component: CommunityPollsPage,
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (!role || !COMMUNITY_POLLS_ALLOWED.includes(role)) {
      throw redirect({ to: '/dashboard' });
    }
  }
});

// Lost & Found — visible to every signed-in society role (residents post via
// mobile; admins moderate from web).
const lostFoundRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/lost-found',
  component: LostFoundPage
});

// CCTV cameras — operational module owned by admins, building manager, and
// committee oversight (president/secretary). Accountant + treasurer are
// finance-only.
const CAMERA_ROLES = [
  'admin',
  'super_admin',
  'president',
  'secretary',
  'building_manager'
] as const;
const guardCameras = (): void => {
  const role = useAuthStore.getState().user?.role;
  if (!role || !(CAMERA_ROLES as ReadonlyArray<string>).includes(role)) {
    throw redirect({ to: '/dashboard' });
  }
};

const camerasRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/cameras',
  component: CamerasPage,
  beforeLoad: guardCameras
});

const cameraDetailRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/cameras/$id',
  component: CameraDetailPage,
  beforeLoad: guardCameras
});

// User-self-service surfaces — visible to any authenticated tenant user.
// Notification preferences + sessions are personal; no role gate needed.
const notificationPreferencesRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/me/preferences',
  component: NotificationPreferencesPage
});

const sessionsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/me/sessions',
  component: SessionsPage
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  onboardingRoute,
  vendorPortalRoute,
  superAdminLoginRoute,
  authLayoutRoute.addChildren([
    dashboardRoute,
    residentsRoute,
    residentDetailRoute,
    residentLedgerRoute,
    towersRoute,
    gatesRoute,
    guardsRoute,
    guardDetailRoute,
    visitorLogsRoute,
    frequentVisitorsRoute,
    frequentVisitorDetailRoute,
    helperCategoriesRoute,
    familyRoute,
    noticesRoute,
    societyEventsRoute,
    societyEventDetailRoute,
    communityRoute,
    helpdeskRoute,
    billsRoute,
    chargeTypesRoute,
    oneTimeChargesRoute,
    receiptsRoute,
    amenitiesRoute,
    documentsRoute,
    vehiclesRoute,
    vendorsRoute,
    agenciesRoute,
    agencyDetailRoute,
    staffRoute,
    serviceProvidersRoute,
    serviceProviderCategoriesRoute,
    moveInOutRoute,
    nocsRoute,
    auditLogRoute,
    reportsRoute,
    complianceRoute,
    governanceRoute,
    electionsRoute,
    attendanceRoute,
    settingsRoute,
    parkingRoute,
    parkingZoneDetailRoute,
    parkingDisputesRoute,
    parkingMarketplaceRoute,
    parkingChargersRoute,
    parkingChargerDetailRoute,
    parkingChargerRequestsRoute,
    visitorParkingRoute,
    parkingMapRoute,
    parkingWrongParkerRoute,
    parkingEvStrikesRoute,
    parkingAnalyticsRoute,
    panicIncidentsRoute,
    securityIncidentsRoute,
    patrolRoute,
    chatRoute,
    collectionsRoute,
    collectionDetailRoute,
    communityPollsRoute,
    lostFoundRoute,
    camerasRoute,
    cameraDetailRoute,
    notificationPreferencesRoute,
    sessionsRoute,
    superAdminDashboardRoute,
    superAdminUsersRoute,
    superAdminBillingRoute,
    superAdminPlansRoute,
    superAdminPermissionsRoute,
    superAdminAuditRoute,
    superAdminTenantDetailRoute,
    superAdminMarketingLeadsRoute,
    superAdminHrRoute
  ])
]);

export const router = new Router({
  routeTree,
  history:
    typeof window === 'undefined' ? createMemoryHistory({ initialEntries: ['/'] }) : createBrowserHistory(),
  defaultPreload: 'intent'
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
